import { useMemo, useState, type FormEvent } from "react";
import {
  PARAMETROS_VAZIOS,
  POSICIONAMENTOS,
  SIMULACAO_INICIAL,
  SITUACOES,
  calcular,
  daRespostas,
  doBanco,
  paraOBanco,
  parametrosPreenchidos,
  type CampoSimulacao,
  type Parametros,
  type Simulacao,
} from "../lib/calculadoraIrpf";
import {
  formatarReais,
  salvarCalculo,
  salvarParametros,
  type MapaCalculoLido,
  type ParametrosLidos,
  type Pergunta,
  type Proposta,
} from "../lib/irproposta";
import { mascararInteiro, mascararMoeda, moedaDeNumero } from "../lib/mascaras";
import { CAMPO, Recado, Secao } from "./ui";

// A calculadora de honorários — a aba.
//
// A regra do cálculo NÃO está aqui: está em `lib/calculadoraIrpf.ts`, módulo puro
// com prova em `app/tests/calculadora.mjs`. Este arquivo desenha, e a diferença
// importa — um preço que entra em contrato não pode depender de alguém abrir a
// tela para saber se está certo.
//
// ---------------------------------------------------------------------------
// O que mudou em relação ao app do Lovable
// ---------------------------------------------------------------------------
// Lá são três passos numerados (parâmetros -> simulação -> resultado), com botão
// "Calcular" no fim. Aqui não há passo nem botão: o resultado é recalculado a
// cada toque, e os parâmetros saíram do caminho porque PERSISTEM — são os valores
// da região do aluno, não mudam de cliente para cliente. O assistente de três
// passos existia lá porque os treze valores morriam ao recarregar a página.
//
// O que ganhou seção própria foi o que lá não existia: PARA QUEM é o cálculo. Com
// a proposta escolhida, a simulação nasce preenchida com o que o cliente
// respondeu, e o resultado pode ser gravado na proposta — de onde entra no
// contrato, por extenso.
//
// ---------------------------------------------------------------------------
// Por que a origem de cada campo aparece na tela
// ---------------------------------------------------------------------------
// Dos nove campos da simulação, o formulário de hoje responde CINCO. Os outros
// quatro (criptoativos, atividade rural, exterior, retificação) não têm pergunta
// nenhuma, e vêm desmarcados. Calculadora que mostra tudo igual faria o aluno
// achar que o cliente respondeu "não" a coisas que nunca lhe foram perguntadas —
// e isso é preço apoiado em silêncio, que é o que o princípio 2 proíbe.

type Recadinho = { tipo: "ok" | "erro"; texto: string };

export default function CalculadoraHonorarios({
  propostas,
  questionario,
  mapa,
  parametros,
  alvo,
  aoTrocarAlvo,
  aoRecarregar,
  aoRecarregarParametros,
}: {
  propostas: Proposta[];
  questionario: Pergunta[];
  mapa: MapaCalculoLido[];
  parametros: ParametrosLidos;
  alvo: string | null;
  aoTrocarAlvo: (id: string | null) => void;
  aoRecarregar: () => Promise<void>;
  aoRecarregarParametros: () => Promise<void>;
}) {
  const [abrindoValores, setAbrindoValores] = useState(false);
  const mostrarValores = abrindoValores || !parametros.preenchido;

  const proposta = propostas.find((p) => p.id === alvo) ?? null;

  return (
    <>
      {/* Tom de ATENÇÃO, não de erro: não há nada errado em ainda não ter
          preenchido, e `Recado tipo="erro"` é vermelho com `role="alert"` — que
          anuncia de forma assertiva a quem usa leitor de tela. É o mesmo tom que
          "Faltam dados deste cliente" usa na aba de propostas. */}
      {!parametros.preenchido && (
        <p className="rounded-xl bg-alerta-fundo p-3.5 text-sm leading-relaxed text-alerta-tinta">
          Antes de calcular, preencha os seus <strong>valores de referência</strong> abaixo — pelo
          menos as três bases. Sem eles a conta multiplica zeros.
        </p>
      )}

      <Secao
        titulo="Para qual cliente"
        descricao="Escolha uma proposta para a simulação nascer com as respostas do cliente — e para poder gravar o resultado nela."
        i={1}
      >
        <label className="mt-4 block max-w-lg">
          <span className="mb-1 block text-sm font-semibold text-titulo">Proposta</span>
          <select
            value={alvo ?? ""}
            onChange={(e) => aoTrocarAlvo(e.target.value || null)}
            className={CAMPO}
          >
            <option value="">Simulação livre — não grava em ninguém</option>
            {propostas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nome}
                {p.honorarios !== null ? ` — ${formatarReais(p.honorarios)}` : ""}
              </option>
            ))}
          </select>
        </label>
        {propostas.length === 0 && (
          <p className="mt-3 text-sm text-cinza">
            Você ainda não tem propostas. A simulação livre funciona do mesmo jeito — só não tem
            onde gravar.
          </p>
        )}
      </Secao>

      {/*
        `key` no alvo, e não `useEffect` sincronizando estado.
        Trocar de cliente tem de zerar a simulação inteira, e um efeito que
        observasse a proposta a zeraria TAMBÉM depois de gravar — porque gravar
        recarrega a lista e troca a identidade do objeto. Remontar no alvo faz a
        troca de cliente resetar e o salvamento não mexer em nada.
      */}
      <Simulador
        key={alvo ?? "livre"}
        proposta={proposta}
        questionario={questionario}
        mapa={mapa}
        parametros={parametros}
        aoRecarregar={aoRecarregar}
      />

      <Secao
        titulo="Meus valores de referência"
        descricao="Os valores da SUA região. Ficam salvos e valem para todos os cálculos."
        acao={
          parametros.preenchido ? (
            <button
              type="button"
              onClick={() => setAbrindoValores((v) => !v)}
              className="text-sm font-semibold text-link underline decoration-link/30 underline-offset-4 hover:decoration-link"
            >
              {mostrarValores ? "Esconder" : "Ver e ajustar"}
            </button>
          ) : undefined
        }
        i={3}
      >
        {mostrarValores ? (
          <ValoresDaRegiao
            parametros={parametros}
            aoSalvar={async () => {
              await aoRecarregarParametros();
              setAbrindoValores(false);
            }}
          />
        ) : (
          <dl className="mt-4 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-cinza">Simples</dt>
              <dd className="font-semibold text-tinta">{formatarReais(parametros.base_simples)}</dd>
            </div>
            <div>
              <dt className="text-cinza">Intermediária</dt>
              <dd className="font-semibold text-tinta">
                {formatarReais(parametros.base_intermediaria)}
              </dd>
            </div>
            <div>
              <dt className="text-cinza">Complexa</dt>
              <dd className="font-semibold text-tinta">{formatarReais(parametros.base_complexa)}</dd>
            </div>
          </dl>
        )}
      </Secao>
    </>
  );
}

// ---------------------------------------------------------------------------
// A simulação e o resultado, lado a lado
// ---------------------------------------------------------------------------
function Simulador({
  proposta,
  questionario,
  mapa,
  parametros,
  aoRecarregar,
}: {
  proposta: Proposta | null;
  questionario: Pergunta[];
  mapa: MapaCalculoLido[];
  parametros: ParametrosLidos;
  aoRecarregar: () => Promise<void>;
}) {
  // Tudo calculado no primeiro render, uma vez: a proposta escolhida traz o
  // cálculo JÁ GRAVADO (se houver) ou o pré-preenchimento das respostas. O
  // gravado ganha, porque é uma decisão que o aluno tomou.
  const inicial = useMemo(() => {
    if (!proposta) {
      return { simulacao: SIMULACAO_INICIAL, doCliente: [] as CampoSimulacao[], semPergunta: [] as CampoSimulacao[], alertas: [] as string[], recuperado: false };
    }
    const pre = daRespostas(mapa, questionario, proposta.respostas ?? {});
    const gravado = doBanco(proposta.honorarios_detalhe);
    return {
      simulacao: gravado ?? pre.simulacao,
      doCliente: pre.doCliente,
      semPergunta: pre.semPergunta,
      alertas: pre.alertas,
      recuperado: gravado !== null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [s, setS] = useState<Simulacao>(inicial.simulacao);
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<Recadinho | null>(null);

  const r = useMemo(() => calcular(parametros, s), [parametros, s]);

  const veioDoCliente = (campo: CampoSimulacao) => inicial.doCliente.includes(campo);
  const semPergunta = (campo: CampoSimulacao) => inicial.semPergunta.includes(campo);

  async function gravar() {
    if (!proposta) return;
    setSalvando(true);
    setRecado(null);
    const resposta = await salvarCalculo(
      proposta.id,
      r.total,
      paraOBanco(r, s, inicial.doCliente),
    );
    setSalvando(false);
    if (!resposta.ok) return setRecado({ tipo: "erro", texto: resposta.mensagem });
    await aoRecarregar();
    setRecado({
      tipo: "ok",
      texto: `${formatarReais(r.total)} gravado na proposta de ${proposta.nome}. Já entra no contrato, por extenso.`,
    });
  }

  async function imprimir() {
    // `window.open` SÍNCRONO, dentro do clique — é o que o bloqueador de pop-up
    // deixa passar. A ponte `/abrir/:curso/:card` existe justamente porque
    // `window.open` DEPOIS de um `await` é bloqueado; aqui não há await antes.
    // O `import()` fica depois da janela aberta, pelo mesmo motivo.
    const janela = window.open("", "_blank");
    if (!janela) {
      return setRecado({
        tipo: "erro",
        texto: "O navegador bloqueou a janela de impressão. Libere os pop-ups deste site e tente de novo.",
      });
    }
    const { htmlDoResumo } = await import("../lib/resumoHonorarios");
    janela.document.write(
      htmlDoResumo(r, {
        cliente: proposta?.nome ?? null,
        escritorio: null,
        ano_calendario: proposta?.ano_calendario ?? null,
        data: new Date().toLocaleDateString("pt-BR"),
      }),
    );
    janela.document.close();
    janela.focus();
    janela.print();
  }

  return (
    <Secao
      titulo="A declaração deste cliente"
      descricao={
        proposta
          ? inicial.recuperado
            ? "Recarregada do cálculo que você já gravou nesta proposta."
            : "Preenchida com o que o cliente respondeu. Ajuste o que ele não foi perguntado."
          : "Marque a situação do cliente. O resultado muda na hora."
      }
      i={2}
    >
      {inicial.alertas.length > 0 && (
        <div className="mt-4 space-y-2">
          {inicial.alertas.map((a) => (
            <p
              key={a}
              className="rounded-xl bg-alerta-fundo p-3.5 text-sm leading-relaxed text-alerta-tinta"
            >
              {a}
            </p>
          ))}
        </div>
      )}

      <div className="mt-5 grid gap-6 lg:grid-cols-2">
        {/* Coluna 1 — a situação */}
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 flex items-center gap-2 text-sm font-semibold text-titulo">
                Dependentes
                {veioDoCliente("dependentes") && <SeloOrigem>do cliente</SeloOrigem>}
              </span>
              <input
                value={String(s.dependentes)}
                onChange={(e) =>
                  setS({ ...s, dependentes: Number(mascararInteiro(e.target.value, 2) || 0) })
                }
                inputMode="numeric"
                className={CAMPO}
              />
            </label>
            <label className="block">
              <span className="mb-1 flex items-center gap-2 text-sm font-semibold text-titulo">
                Fontes de renda
                <SeloOrigem>sugestão</SeloOrigem>
              </span>
              <input
                value={String(s.fontes_renda)}
                onChange={(e) =>
                  setS({
                    ...s,
                    fontes_renda: Math.max(1, Number(mascararInteiro(e.target.value, 2) || 1)),
                  })
                }
                inputMode="numeric"
                className={CAMPO}
              />
              <span className="mt-1 block text-xs leading-relaxed text-cinza">
                Contamos os "Sim" do formulário. A pergunta 4 é um pacote (salário, pró-labore,
                lucros, aposentadoria) e pode valer mais de uma — confira.
              </span>
            </label>
          </div>

          <label className="block">
            <span className="mb-1 block text-sm font-semibold text-titulo">
              Seu posicionamento profissional
            </span>
            <select
              value={s.posicionamento}
              onChange={(e) =>
                setS({ ...s, posicionamento: e.target.value as Simulacao["posicionamento"] })
              }
              className={CAMPO}
            >
              {POSICIONAMENTOS.map((p) => (
                <option key={p.chave} value={p.chave}>
                  {p.rotulo} ({p.pct > 0 ? "+" : ""}
                  {Math.round(p.pct * 100)}%)
                </option>
              ))}
            </select>
          </label>

          <section role="group" aria-labelledby="situacoes-especiais">
            <h3 id="situacoes-especiais" className="text-sm font-semibold text-titulo">
              Situações especiais
            </h3>
            <div className="mt-2 divide-y divide-borda">
              {SITUACOES.map((x) => (
                <label
                  key={x.campo}
                  className="flex min-h-11 cursor-pointer items-center justify-between gap-3 py-1"
                >
                  <span className="flex flex-wrap items-center gap-2 text-sm text-tinta">
                    {x.rotulo}
                    {veioDoCliente(x.campo) && <SeloOrigem>do cliente</SeloOrigem>}
                    {proposta && semPergunta(x.campo) && <SeloOrigem>você decide</SeloOrigem>}
                  </span>
                  <input
                    type="checkbox"
                    checked={s[x.campo]}
                    onChange={(e) => setS({ ...s, [x.campo]: e.target.checked })}
                    className="size-4 shrink-0 accent-laranja"
                  />
                </label>
              ))}
            </div>
          </section>
        </div>

        {/* Coluna 2 — o resultado */}
        <div className="space-y-4">
          <div className="rounded-xl bg-superficie-2 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-chip px-3 py-1 text-xs font-bold text-chip-tinta">
                {r.classificacao}
              </span>
              <span className="text-sm text-cinza">{r.motivo}</span>
            </div>

            <table className="mt-3 w-full text-sm">
              <tbody>
                <Linha rotulo={`Base — declaração ${r.classificacao.toLowerCase()}`} valor={r.base} />
                {r.adicionais.map((a) => (
                  <Linha key={a.descricao} rotulo={a.descricao} valor={a.valor} sutil />
                ))}
                {r.adicionais.length === 0 && (
                  <tr>
                    <td colSpan={2} className="py-1.5 text-cinza">
                      Nenhum adicional
                    </td>
                  </tr>
                )}
                <Linha
                  rotulo={`${r.posicionamento_rotulo} (${r.posicionamento_pct > 0 ? "+" : ""}${Math.round(r.posicionamento_pct * 100)}%)`}
                  valor={r.ajuste}
                />
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border-2 border-azul p-5 text-center">
            <p className="text-xs font-semibold tracking-wide text-cinza uppercase">
              Honorário sugerido
            </p>
            <p className="font-display mt-1 text-3xl font-bold text-titulo tabular-nums">
              {formatarReais(r.total)}
            </p>
            <p className="mt-1 text-sm text-cinza">
              Faixa para negociar: {formatarReais(r.faixa_min)} a {formatarReais(r.faixa_max)}
            </p>
          </div>

          <p className="rounded-xl bg-superficie-2 p-3.5 text-sm leading-relaxed text-cinza">
            {r.observacao}
          </p>

          <div className="flex flex-wrap gap-2">
            {proposta && (
              <button
                type="button"
                onClick={gravar}
                disabled={salvando || r.total <= 0}
                className="pill-cta disabled:opacity-60"
              >
                {salvando ? "Gravando…" : `Gravar na proposta de ${primeiroNome(proposta.nome)}`}
              </button>
            )}
            <button
              type="button"
              onClick={imprimir}
              disabled={r.total <= 0}
              className="btn-contorno disabled:opacity-60"
            >
              Imprimir resumo
            </button>
          </div>

          {!proposta && (
            <p className="text-sm text-cinza">
              Esta é uma simulação livre. Escolha uma proposta acima se quiser gravar o valor.
            </p>
          )}

          {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
        </div>
      </div>
    </Secao>
  );
}

function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] || nome;
}

function SeloOrigem({ children }: { children: string }) {
  return (
    <span className="rounded-full bg-tile px-2 py-0.5 text-[11px] font-semibold text-link">
      {children}
    </span>
  );
}

function Linha({
  rotulo,
  valor,
  sutil,
}: {
  rotulo: string;
  valor: number;
  sutil?: boolean;
}) {
  return (
    <tr>
      <td className={`py-1.5 pr-3 ${sutil ? "text-cinza" : "text-tinta"}`}>{rotulo}</td>
      <td className="py-1.5 text-right font-semibold text-tinta tabular-nums whitespace-nowrap">
        {formatarReais(valor)}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Os treze valores da região
// ---------------------------------------------------------------------------
// Sem valor sugerido, e isto é decisão de produto, não campo esquecido: o
// repositório de origem ZEROU os treze números que a primeira versão trazia,
// porque "a ferramenta serve como referência estratégica de precificação, não
// como tabela fixa nacional". O texto de ajuda abaixo é o daquela tela.
const CAMPOS_DE_VALOR: ReadonlyArray<{ chave: keyof Parametros; rotulo: string; grupo: "base" | "adicional" }> = [
  { chave: "base_simples", rotulo: "Declaração simples", grupo: "base" },
  { chave: "base_intermediaria", rotulo: "Declaração intermediária", grupo: "base" },
  { chave: "base_complexa", rotulo: "Declaração complexa", grupo: "base" },
  { chave: "ad_dependente", rotulo: "Por dependente", grupo: "adicional" },
  { chave: "ad_fonte_renda", rotulo: "Por fonte de renda extra", grupo: "adicional" },
  { chave: "ad_aluguel", rotulo: "Rendimentos de aluguel", grupo: "adicional" },
  { chave: "ad_ganho_capital", rotulo: "Ganho de capital", grupo: "adicional" },
  { chave: "ad_bolsa", rotulo: "Operações em bolsa", grupo: "adicional" },
  { chave: "ad_cripto", rotulo: "Criptoativos", grupo: "adicional" },
  { chave: "ad_carne_leao", rotulo: "Carnê-leão", grupo: "adicional" },
  { chave: "ad_atividade_rural", rotulo: "Atividade rural", grupo: "adicional" },
  { chave: "ad_exterior", rotulo: "Rendimentos no exterior", grupo: "adicional" },
  { chave: "ad_retificacao", rotulo: "Retificação de declaração", grupo: "adicional" },
];

function ValoresDaRegiao({
  parametros,
  aoSalvar,
}: {
  parametros: ParametrosLidos;
  aoSalvar: () => Promise<void>;
}) {
  /**
   * Dois estados por campo, como nos honorários da proposta: o TEXTO que aparece
   * ("R$ 250,00") e o NÚMERO que a RPC recebe (250). Guardar só o texto obrigaria
   * a desmascarar no submit, e é ali que "R$ 1.234,56" viraria `NaN` — treze
   * vezes, num valor que multiplica todos os cálculos seguintes.
   */
  const [texto, setTexto] = useState<Record<string, string>>(() => {
    const inicial: Record<string, string> = {};
    for (const c of CAMPOS_DE_VALOR) {
      const v = parametros[c.chave];
      inicial[c.chave] = v > 0 ? moedaDeNumero(v) : "";
    }
    return inicial;
  });
  const [numero, setNumero] = useState<Parametros>(() => {
    const inicial = { ...PARAMETROS_VAZIOS };
    for (const c of CAMPOS_DE_VALOR) inicial[c.chave] = parametros[c.chave];
    return inicial;
  });
  const [salvando, setSalvando] = useState(false);
  const [recado, setRecado] = useState<Recadinho | null>(null);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setRecado(null);

    // Avisa aqui em vez de deixar o banco recusar: sem as três bases a conta
    // multiplica zeros, e a mensagem do Postgres não diz qual base falta.
    if (!parametrosPreenchidos(numero)) {
      return setRecado({
        tipo: "erro",
        texto: "As três bases (simples, intermediária e complexa) precisam de valor. Os adicionais podem ficar em zero.",
      });
    }

    setSalvando(true);
    const r = await salvarParametros(numero);
    setSalvando(false);
    if (!r.ok) return setRecado({ tipo: "erro", texto: r.mensagem });
    await aoSalvar();
  }

  const campo = (c: (typeof CAMPOS_DE_VALOR)[number]) => (
    <label key={c.chave} className="block">
      <span className="mb-1 block text-sm font-semibold text-titulo">{c.rotulo}</span>
      <input
        value={texto[c.chave] ?? ""}
        onChange={(e) => {
          const m = mascararMoeda(e.target.value);
          setTexto((t) => ({ ...t, [c.chave]: m.texto }));
          setNumero((n) => ({ ...n, [c.chave]: m.numero ?? 0 }));
        }}
        inputMode="numeric"
        placeholder="R$ 0,00"
        className={CAMPO}
      />
    </label>
  );

  return (
    // O `id` não é enfeite: sete destes rótulos são IGUAIS aos das situações
    // especiais logo acima ("Ganho de capital", "Criptoativos", "Carnê-leão"…), e
    // sem um escopo nomeado a prova em Chrome (`app/tests/calculadora-tela.mjs`)
    // encontrava o checkbox em vez do campo de valor — e passava, escrevendo em
    // lugar nenhum. Teste que acerta por sorteio é a cicatriz do desempate por
    // uuid; aqui ela foi evitada dando nome ao formulário.
    <form id="valores-da-regiao" onSubmit={enviar} className="mt-4">
      <p className="rounded-xl bg-superficie-2 p-3.5 text-sm leading-relaxed text-cinza">
        Ajuste os valores conforme a <strong>sua</strong> região. A ferramenta é referência
        estratégica de precificação, não tabela fixa nacional — por isso não vem com número
        nenhum sugerido.
      </p>

      <h3 className="mt-5 text-sm font-semibold text-titulo">Honorários base</h3>
      <div className="mt-2 grid gap-3 sm:grid-cols-3">
        {CAMPOS_DE_VALOR.filter((c) => c.grupo === "base").map(campo)}
      </div>

      <h3 className="mt-5 text-sm font-semibold text-titulo">Adicionais de complexidade</h3>
      <div className="mt-2 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {CAMPOS_DE_VALOR.filter((c) => c.grupo === "adicional").map(campo)}
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <button type="submit" disabled={salvando} className="pill-cta disabled:opacity-60">
          {salvando ? "Salvando…" : "Salvar meus valores"}
        </button>
      </div>

      {recado && <Recado tipo={recado.tipo} texto={recado.texto} />}
    </form>
  );
}
